<template>
  <ListComponent title="Non Bonded Containers" :totalCount="totalItems">
    <template #list>
      <v-row class="ma-0">
        <v-col cols="12" md="4" class="pl-0 pt-0 pb-2 d-flex align-center" style="gap:8px">
          <v-text-field
            label="Search (Container No)"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            style="min-width:150px"
            v-model="search"
            @click:clear="onSearchClear"
            @keyup.enter="onSearch"
          />
        </v-col>
        <v-col cols="12" md="8" class="pt-0 pb-2 d-flex align-center">
          <v-tabs height="35" v-model="tab">
            <v-tabs-slider></v-tabs-slider>
            <v-tab>
              Pending
              <v-badge v-if="statusCounts.Pending" top :content="statusCounts.Pending" overlap class="mb-4 ml-3">
              </v-badge>
            </v-tab>
            <v-tab>
              Assigned
              <v-badge v-if="statusCounts.Assigned" top :content="statusCounts.Assigned" overlap class="mb-4 ml-3">
              </v-badge>
            </v-tab>
            <v-tab>
              Completed
              <v-badge v-if="statusCounts.Completed" top :content="statusCounts.Completed" overlap class="mb-4 ml-3">
              </v-badge>
            </v-tab>
            <v-tab>
              Cancelled
              <v-badge v-if="statusCounts.Cancelled" top :content="statusCounts.Cancelled" overlap class="mb-4 ml-3">
              </v-badge>
            </v-tab>
          </v-tabs>
        </v-col>
        <v-col cols="12" md="12" class="pr-0 pt-0 pb-2 d-flex align-center justify-end">
          <Pagination
            v-if="totalItems > 0"
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>
        <v-col cols="12" class="pa-0">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 190px)"
            class="ag-theme-alpine"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :rowData="allList"
          />
        </v-col>
      </v-row>
    </template>
  </ListComponent>
</template>
<script>
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import Pagination from "@/components/General/Pagination.vue";
import { isAuthenticated, hasRouteAccess, getPermissions } from "@/utils/functions";

const STATUS_COLORS = {
  Pending: "#9E9E9E",
  Assigned: "#1976D2",
  Completed: "#388E3C",
  Cancelled: "#D32F2F",
};

export default {
  components: {
    ListComponent,
    agColumnHeader: CustomHeader,
    AgGridVue,
    Pagination,
  },
  data() {
    return {
      allList: [],
      totalItems: 0,
      search: "",
      tab: 0,
      statusCounts: {},
      statusByTab: ["Pending", "Assigned", "Completed", "Cancelled"],
      pageNo: 1,
      itemsPerPage: 50,
      permissions: null,
      defaultColDef: { resizable: true },
      gridOptions: {
        onGridSizeChanged: () => this.gridOptions.api.sizeColumnsToFit(),
        enableCellTextSelection: true,
        rowHeight: 35,
      },
      headers: [
        {
          headerName: "Container No",
          field: "container_number",
          sortable: true,
          minWidth: 180,
        },
        {
          headerName: "Container Type",
          field: "container_type_name",
          sortable: true,
          minWidth: 150,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Status",
          field: "status",
          sortable: true,
          minWidth: 130,
          cellStyle: (params) => ({
            color: STATUS_COLORS[params.value] || "inherit",
            fontWeight: "bold",
          }),
        },
        {
          headerName: "Trip ID",
          field: "trip_id",
          sortable: true,
          minWidth: 150,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Route",
          field: "lane_title",
          sortable: true,
          minWidth: 150,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Driver",
          field: "driver_name",
          sortable: true,
          minWidth: 160,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Vehicle",
          field: "vehicle_number",
          sortable: true,
          minWidth: 130,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Gate In Received",
          field: "gate_in_received_on",
          sortable: true,
          minWidth: 170,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Completed On",
          field: "completed_on",
          sortable: true,
          minWidth: 170,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Uploaded On",
          field: "created",
          sortable: true,
          minWidth: 160,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Uploaded By",
          field: "created_by",
          sortable: true,
          minWidth: 160,
          valueFormatter: (params) => params.value || "-",
        },
      ],
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
  },
  watch: {
    tab() {
      this.pageNo = 1;
      this.getList();
      this.getStatusCounts();
    },
  },
  methods: {
    onSearch() {
      this.pageNo = 1;
      this.getList();
    },
    onSearchClear() {
      this.search = "";
      this.pageNo = 1;
      this.getList();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getList();
    },
    prevPage() {
      this.pageNo--;
      this.getList();
    },
    nextPage() {
      this.pageNo++;
      this.getList();
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
    },
    getList() {
      this.$bus.$emit("showLoader", true);
      const params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        status: this.statusByTab[this.tab],
      };
      if (this.search) {
        params.search = this.search;
      }
      this.$api.nonBondedContainer
        .getNonBondedContainerList(params)
        .then((res) => {
          this.allList = res.data;
          this.totalItems = res.count != null ? res.count : res.data.length;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    getStatusCounts() {
      const params = {};
      if (this.search) {
        params.search = this.search;
      }
      this.$api.nonBondedContainer
        .getNonBondedContainerStatusCounts(params)
        .then((res) => {
          this.statusCounts = res.data || res;
        })
        .catch((err) => {
          console.error(err);
        });
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("emergency_order_container");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this module!",
          color: "error",
        });
        this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
      } else {
        this.permissions = { ...permissions };
        this.getList();
        this.getStatusCounts();
      }
    }
  },
};
</script>
